import { buildSort, goPromise } from "@delanobgt/admin-core/util/helpers";
import {
  ApiClientFactory,
  IMeta,
  IPagination,
  ISort,
} from "@delanobgt/admin-core/util/types";
import { CancelTokenSource } from "axios";
import _ from "lodash";

import { EExpenseSortField, IExpense, PExpenseFilter } from "../entities";
import IExpenseRepository from "../repository";

export default class ExpenseRepository
  implements IExpenseRepository<CancelTokenSource> {
  private apiClientFactory: ApiClientFactory;

  // api routes
  private ROUTE = "/admin/expense";

  constructor(apiClientFactory: ApiClientFactory) {
    this.apiClientFactory = apiClientFactory;
  }

  buildFilter = (filter: PExpenseFilter) => {
    const created_at = filter.created_at_enabled
      ? `${filter.created_at_start},${filter.created_at_end}`
      : undefined;

    const clonedFilter = { ...filter };
    delete clonedFilter.created_at_enabled;
    delete clonedFilter.created_at_start;
    delete clonedFilter.created_at_end;

    return _.pickBy(
      {
        ...clonedFilter,
        created_at,
      },
      Boolean
    );
  };

  private getSignalToken(signal: CancelTokenSource) {
    return _.get(signal, "token", null);
  }

  private getError(error: any) {
    return _.get(error, "response.data.errors", "Something went wrong.");
  }

  async getExpense(
    pagination: IPagination,
    filter: PExpenseFilter,
    sorts: ISort<EExpenseSortField>[],
    signal?: CancelTokenSource
  ): Promise<[IExpense[], IMeta]> {
    const sort = buildSort(sorts);
    const params = _.pickBy(
      {
        ...pagination,
        ...this.buildFilter(filter),
      },
      Boolean
    );
    const [err, response] = await goPromise(
      this.apiClientFactory({ cancelToken: this.getSignalToken(signal) }).get(
        `${this.ROUTE}?sort=${sort}`,
        {
          params,
        }
      )
    );
    if (err) {
      throw this.getError(err);
    }
    const { data, meta } = response.data;
    return [data, meta];
  }

  async getExpenseById(id: number): Promise<IExpense> {
    const [err, response] = await goPromise(
      this.apiClientFactory().get(`${this.ROUTE}/${id}`)
    );
    if (err) {
      throw this.getError(err);
    }
    return response.data;
  }

  async getExpenseExcelData(month: string, year: string): Promise<any> {
    const [err, response] = await goPromise(
      this.apiClientFactory({
        responseType: "blob",
      }).get(`${this.ROUTE}/download`, {
        params: { month, year },
      })
    );
    if (err) {
      throw this.getError(err);
    }
    return response.data;
  }
}
